import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useCelebritySearch } from "../../hooks/useCelebritySearch";
import type { Celebrity } from "../../lib/types";
import { SearchResult } from "./SearchResult";

interface SearchModalProps {
  open: boolean;
  onClose: () => void;
  onSelect: (celebrity: Celebrity) => void;
}

export function SearchModal({ open, onClose, onSelect }: SearchModalProps) {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const { results, isLoading, search } = useCelebritySearch();

  // Auto-focus and reset when opened
  useEffect(() => {
    if (open) {
      setQuery("");
      setActiveIndex(-1);
      search("");
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open, search]);

  // Reset active index when results change
  useEffect(() => {
    setActiveIndex(-1);
  }, []);

  // Scroll active item into view
  useEffect(() => {
    if (activeIndex < 0) return;
    document
      .getElementById(`search-result-${activeIndex}`)
      ?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  const handleSelect = useCallback(
    (celebrity: Celebrity) => {
      onSelect(celebrity);
      onClose();
    },
    [onSelect, onClose],
  );

  // Keyboard navigation
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIndex((i) => Math.min(i + 1, results.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIndex((i) => Math.max(i - 1, -1));
      } else if (e.key === "Enter" && activeIndex >= 0) {
        e.preventDefault();
        const celebrity = results[activeIndex];
        if (celebrity) handleSelect(celebrity as Celebrity);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose, results, activeIndex, handleSelect]);

  function handleQueryChange(value: string) {
    setQuery(value);
    search(value);
  }

  if (!open) return null;

  const showResults = query.trim().length >= 2;

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions lint/a11y/useKeyWithClickEvents: backdrop dismiss is supplementary — modal has a close button
    <div
      className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center"
      onClick={onClose}
    >
      {/* biome-ignore lint/a11y/noStaticElementInteractions lint/a11y/useKeyWithClickEvents: stop propagation to prevent backdrop close */}
      <div
        className="bg-white dark:bg-stone-900 rounded-2xl p-6 w-full max-w-md mx-4 transition-all border border-stone-200 dark:border-transparent"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header row */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-stone-900 dark:text-stone-100 font-semibold text-lg">
            {t("search.heading")}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-stone-400 dark:text-stone-500 hover:text-stone-700 dark:hover:text-stone-200 transition-colors text-lg leading-none cursor-pointer"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => handleQueryChange(e.target.value)}
          placeholder={t("search.placeholder")}
          className="w-full bg-stone-100 dark:bg-stone-800 rounded-lg px-4 py-2 text-stone-900 dark:text-stone-100 outline-none focus:ring-2 focus:ring-amber-500 placeholder:text-stone-400 dark:placeholder:text-stone-500"
        />

        {/* Result count */}
        {!isLoading && showResults && results.length > 0 && (
          <p className="text-stone-400 dark:text-stone-600 text-xs mt-3 px-1">
            {t("search.result", { count: results.length })}
          </p>
        )}

        <div className="mt-2 max-h-80 overflow-y-auto">
          {isLoading && (
            <p className="text-stone-500 dark:text-stone-400 text-sm px-3 py-2">
              {t("search.loading")}
            </p>
          )}
          {!isLoading && showResults && results.length === 0 && (
            <p className="text-stone-500 dark:text-stone-400 text-sm px-3 py-2">
              {t("search.noResults")}
            </p>
          )}
          {!isLoading &&
            results.map((celebrity, i) => (
              <SearchResult
                key={celebrity.id}
                id={`search-result-${i}`}
                celebrity={celebrity as Celebrity}
                isActive={i === activeIndex}
                onSelect={() => handleSelect(celebrity as Celebrity)}
              />
            ))}
        </div>
      </div>
    </div>
  );
}
